import { useState, useEffect, useRef, useCallback, useMemo, type JSX } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Users, UserCircle } from 'lucide-react'
import ConversationInbox, { type InboxFilter, type InboxScope, type InboxSelectionMeta } from '../../../components/conversations/ConversationInbox.js'
import { type NewConversationData } from '../../../components/conversations/CreateConversationPanel.js'
import { useInboxConversations, type AdvancedFilters } from '../../../hooks/useInboxConversations.js'
import { useAuthStore } from '../../../store/authStore.js'
import { api } from '../../../api/client.js'
import { useReassignDrawerController } from '../../../components/reassignment/useReassignDrawerController.js'
import { useReassignmentDropdowns } from '../../../hooks/useReassignmentDropdowns.js'
import ReassignDrawer from '../../../components/reassignment/ReassignDrawer.js'

/**
 * The single conversation inbox.
 *
 * Replaces the six routes that used to exist (campaign/my × inbox/nearing-sla/
 * beyond-sla) — audience is a tab (`?scope=`), SLA state is one of the inbox's
 * own filters (`?filter=`). Both live in the URL so notifications, search
 * results and the old routes can deep-link straight into the right view.
 */
const STORAGE_KEY = 'kc3_inbox_tour_seen'

type Scope = 'mine' | 'all'

const SCOPE_META: Record<Scope, { label: string; subtitle: string; icon: JSX.Element }> = {
  mine: { label: 'My Conversations', subtitle: 'Conversations assigned to you or your queues', icon: <UserCircle className="h-3.5 w-3.5" /> },
  all: { label: 'Campaign Conversations', subtitle: 'Conversations in the campaigns you belong to', icon: <Users className="h-3.5 w-3.5" /> },
}

const VALID_FILTERS: InboxFilter[] = ['all', 'unread', 'read', 'starred', 'closed', 'nearing-sla', 'beyond-sla', 'archived']

function parseScope(v: string | null): Scope {
  return v === 'all' ? 'all' : 'mine'
}

function parseFilter(v: string | null): InboxFilter {
  return VALID_FILTERS.includes(v as InboxFilter) ? (v as InboxFilter) : 'all'
}

export default function UnifiedInboxPage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const userId = useAuthStore((s) => s.user?.sub) ?? ''

  const [viewLoaded, setViewLoaded] = useState(false)
  const user = useAuthStore((s) => s.user)
  // Mirrors RequirePermission.tsx's hasPermission logic (isSuperAdmin ||
  // isPlatformAdmin || wildcard || permission-in-list). The bulk-action bar's
  // Reassign / Bulk Action buttons hit the reassign endpoint (needs
  // conversations:reassign); Bulk Archive/Unarchive needs conversations:bulk_archive.
  const isSuperAdmin = user?.isSuperAdmin ?? false
  const isPlatformAdmin = user?.roles?.some((r) => r === 'PLATFORM_ADMIN' || r === 'PLATFORM_SUPER_ADMIN') ?? false
  const hasWildcard = user?.permissions?.includes('*') ?? false
  const hasPerm = (p: string): boolean =>
    isSuperAdmin || isPlatformAdmin || hasWildcard || (user?.permissions?.includes(p) ?? false)
  const canTransfer = hasPerm('conversations:reassign')
  const canBulkTransfer = hasPerm('conversations:transfer')
  const canArchive = hasPerm('conversations:bulk_archive')
  // Bulk Action's lifecycle Status field goes through close/reopen.
  const canChangeStatus = hasPerm('conversations:close')
  const canViewAll = isSuperAdmin || isPlatformAdmin || hasWildcard || hasPerm('conversations:view_all')

  const rawScope = searchParams.get('scope')
  const defaultScope: Scope = canViewAll ? 'all' : 'mine'
  const scope: Scope = rawScope === 'all' || rawScope === 'mine' ? rawScope : defaultScope
  const urlFilter = parseFilter(searchParams.get('filter'))

  // "Mine" narrows to the signed-in agent (assigned directly or via a queue
  // they belong to). "Campaign Conversations" narrows to the campaigns the
  // user is a member of — enforced server-side, so paging and counts agree
  // and it can't be bypassed by editing the query string.
  // Admins/supervisors with view_all can view all campaign conversations.
  const baseQuery = useMemo(() => {
    if (!userId) return ''
    if (scope === 'mine') {
      return `assignedAgentIdOrQueueMember=${encodeURIComponent(userId)}`
    }
    return canViewAll ? '' : `campaignMemberUserId=${encodeURIComponent(userId)}`
  }, [scope, userId, canViewAll])

  const {
    conversations, totalCount, filterCounts, statusCounts, statusInfo, loading, refetch,
    setFilter, search, setSearch, fromDate, toDate, setDateRange, channel, setChannel, campaignIds, setCampaignIds,
    advancedFilters, setAdvancedFilters, clearAdvancedFilters, page, pageSize, setPage, filterParams,
    showChildTickets, setShowChildTickets, showSecondaryTickets, setShowSecondaryTickets,
    markRead, markUnread, toggleStar, archive, unarchive, bulkArchive, bulkUnarchive,
  } = useInboxConversations(baseQuery, urlFilter, { enabled: Boolean(userId && viewLoaded) })

  const { data: dropdowns } = useReassignmentDropdowns()
  const drawerController = useReassignDrawerController()

  // Bumped after a reassign / bulk-transfer succeeds so the inbox clears its
  // selection (and the "N selected" header disappears).
  const [clearSelectionSignal, setClearSelectionSignal] = useState(0)
  // Declared before `refreshScopeTotals` exists, so the success handler reaches
  // it through a ref rather than forcing the callback to be hoisted.
  const refreshScopeTotalsRef = useRef<() => Promise<void>>(async () => {})
  useEffect(() => {
    drawerController.onSuccess(() => {
      void refetch()
      // A bulk assign moves conversations *between* the Mine and All scopes without
      // necessarily changing either list's length, so the totalCount-watcher
      // below can't see it. Refresh the badges explicitly.
      void refreshScopeTotalsRef.current()
      setClearSelectionSignal((n) => n + 1)
    })
  }, [drawerController, refetch])

  // A parent can't be closed while its children are open, so a bulk selection
  // that includes one drops the drawer's Status field; children alone keep it.
  const [selectionHasParent, setSelectionHasParent] = useState(false)
  const handleReassign = (conversationIds: string[], currentAssigneeAgentIds: string[]): void => {
    setSelectionHasParent(false)
    drawerController.openDrawer(conversationIds, currentAssigneeAgentIds)
  }
  const handleBulkTransfer = (conversationIds: string[], currentAssigneeAgentIds: string[], meta: InboxSelectionMeta): void => {
    setSelectionHasParent(meta.includesParent)
    drawerController.openDrawer(conversationIds, currentAssigneeAgentIds)
  }

  const [scopeTotals, setScopeTotals] = useState<Record<Scope, number | undefined>>({ mine: 0, all: 0 })
  const [firstVisit, setFirstVisit] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== '1' } catch { return true }
  })

  // The page to restore once the saved view is fully applied. Writing the
  // saved scope/filter back into the URL cascades into effects that each
  // force the page to 1 (setFilter, the hook's baseQuery reset) — and the URL
  // update can land in a later commit than viewLoaded, so the page can't be
  // applied inside this .then or purely by declaration order. It's stored
  // here with the view it belongs to and applied once the URL settles.
  const restoredPageRef = useRef<{ page: number; pageSize: number; scope: Scope; filter: InboxFilter } | null>(null)

  // Load the saved inbox view once on mount (or when the user becomes known).
  // If the URL carries no scope/filter, apply the saved values so the agent
  // lands back on their preferred view. Non-URL filters (channel, search,
  // campaignIds, advancedFilters, date range) are restored to the hook state.
  useEffect(() => {
    if (viewLoaded || !userId) return
    let cancelled = false
    api.get<{ view: {
      scope?: string; filter?: string; channel?: string; search?: string;
      campaignIds?: string[]; advancedFilters?: Record<string, unknown>;
      dateFrom?: string; dateTo?: string; page?: number; pageSize?: number;
      showChildTickets?: boolean; showSecondaryTickets?: boolean;
    } }>('/agent/me/conversation-view')
      .then(({ view }) => {
        if (cancelled) return
        const savedScope = view?.scope === 'all' || view?.scope === 'mine' ? view.scope : defaultScope
        const savedFilter = parseFilter(view?.filter ?? null)
        const hasScope = searchParams.get('scope')
        const hasFilter = searchParams.get('filter')
        if (!hasScope && !hasFilter) {
          const next = new URLSearchParams(searchParams)
          if (savedScope === defaultScope) next.delete('scope')
          else next.set('scope', savedScope)
          if (savedFilter === 'all') next.delete('filter')
          else next.set('filter', savedFilter)
          setSearchParams(next, { replace: true })
        }
        // Non-URL durable preferences — all filters are restored so the agent
        // lands back on their exact previous view after opening a ticket.
        if (view?.channel) setChannel(view.channel)
        if (view?.search) setSearch(view.search)
        if (view?.dateFrom || view?.dateTo) setDateRange(view?.dateFrom ?? '', view?.dateTo ?? '')
        if (Array.isArray(view?.campaignIds) && view.campaignIds.length > 0) setCampaignIds(view.campaignIds)
        if (view?.advancedFilters && typeof view.advancedFilters === 'object') {
          setAdvancedFilters(view.advancedFilters as Partial<AdvancedFilters>)
        }
        if (typeof view?.showChildTickets === 'boolean') setShowChildTickets(view.showChildTickets)
        if (typeof view?.showSecondaryTickets === 'boolean') setShowSecondaryTickets(view.showSecondaryTickets)

        const savedPage = typeof view?.page === 'number' ? Math.max(1, Math.floor(view.page)) : 1
        const savedPageSize = typeof view?.pageSize === 'number' ? view.pageSize : pageSize
        const applySavedView = !hasScope && !hasFilter
        const effectiveScope = hasScope ? scope : (applySavedView ? savedScope : defaultScope)
        const effectiveFilter = hasFilter ? urlFilter : (applySavedView ? savedFilter : 'all')
        restoredPageRef.current = {
          page: savedScope === effectiveScope && savedFilter === effectiveFilter ? savedPage : 1,
          pageSize: savedPageSize,
          scope: effectiveScope,
          filter: effectiveFilter,
        }
        setViewLoaded(true)
      })
      .catch(() => setViewLoaded(true))
    return () => { cancelled = true }
  }, [viewLoaded, userId, searchParams, setSearchParams, defaultScope, scope, urlFilter, setChannel, pageSize, setSearch, setDateRange, setCampaignIds, setAdvancedFilters, setShowChildTickets, setShowSecondaryTickets])

  // Persist view changes (scope + filter + channel + pagination) back to the
  // user row. Debounced to avoid rapid API calls.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!viewLoaded || !userId) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      api.put('/agent/me/conversation-view', {
        view: {
          scope, filter: urlFilter, channel,
          search, dateFrom: fromDate, dateTo: toDate,
          campaignIds, advancedFilters,
          showChildTickets, showSecondaryTickets,
          page, pageSize,
        },
      }).catch(() => {})
    }, 500)
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
  }, [viewLoaded, userId, scope, urlFilter, channel, search, fromDate, toDate, campaignIds, advancedFilters, showChildTickets, showSecondaryTickets, page, pageSize])

  // Flush the latest payload on unmount so the last view is always preserved.
  const latestViewRef = useRef<{ ready: boolean; view: Record<string, unknown> }>({ ready: false, view: {} })
  latestViewRef.current = {
    ready: viewLoaded && userId !== '',
    view: {
      scope, filter: urlFilter, channel,
      search, dateFrom: fromDate, dateTo: toDate,
      campaignIds, advancedFilters,
      showChildTickets, showSecondaryTickets,
      page, pageSize,
    },
  }
  useEffect(() => () => {
    const { ready, view } = latestViewRef.current
    if (ready) void api.put('/agent/me/conversation-view', { view }).catch(() => {})
  }, [])

  // Keep the hook's filter in sync with the URL so a deep link lands on the right tab.
  const prevUrlFilter = useRef(urlFilter)
  useEffect(() => {
    if (prevUrlFilter.current === urlFilter) return
    prevUrlFilter.current = urlFilter
    setFilter(urlFilter)
  }, [urlFilter, setFilter])

  // Badge counts for the audience tabs.
  const filterCountField: keyof { all: number; unread: number; read: number; starred: number; closed: number; nearingSla: number; beyondSla: number; archived: number } =
    urlFilter === 'nearing-sla' ? 'nearingSla'
    : urlFilter === 'beyond-sla' ? 'beyondSla'
    : (urlFilter as 'all' | 'unread' | 'read' | 'starred' | 'closed' | 'archived')
  const refreshScopeTotals = useCallback(async (): Promise<void> => {
    const fpPrefix = filterParams ? `&${filterParams}` : ''
    const mineQuery = userId ? `?assignedAgentIdOrQueueMember=${encodeURIComponent(userId)}${fpPrefix}` : (filterParams ? `?${filterParams}` : '')
    const campaignQuery = canViewAll
      ? (filterParams ? `?${filterParams}` : '')
      : (userId ? `?campaignMemberUserId=${encodeURIComponent(userId)}${fpPrefix}` : (filterParams ? `?${filterParams}` : ''))
    const [mine, all] = await Promise.allSettled([
      api.get<{ all: number; unread: number; read: number; starred: number; closed: number; nearingSla: number; beyondSla: number; archived: number }>(`/conversations/counts${mineQuery}`),
      api.get<{ all: number; unread: number; read: number; starred: number; closed: number; nearingSla: number; beyondSla: number; archived: number }>(`/conversations/counts${campaignQuery}`),
    ])
    setScopeTotals({
      mine: mine.status === 'fulfilled' ? mine.value[filterCountField] : undefined,
      all: all.status === 'fulfilled' ? all.value[filterCountField] : undefined,
    })
  }, [userId, canViewAll, filterParams, filterCountField])

  refreshScopeTotalsRef.current = refreshScopeTotals

  // Refresh alongside the list itself — totalCount changing is the cheapest
  // reliable signal that the underlying data moved (incl. ws-driven refetches).
  // Debounce 2s so socket-driven refetch bursts don't fire 2 extra counts
  // queries per bump. Fire on initial mount so scope badges animate from 0.
  const prevTotalCount = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (prevTotalCount.current === totalCount) return
    const prev = prevTotalCount.current
    prevTotalCount.current = totalCount
    if (prev === undefined) {
      // initial mount — fire immediately so scope badges populate
      void refreshScopeTotals()
      return
    }
    const t = setTimeout(() => { void refreshScopeTotals() }, 2000)
    return () => clearTimeout(t)
  }, [refreshScopeTotals, totalCount])

  // Filters changed (search/date/channel/campaign/advanced) — the scope badges
  // must reflect the filtered set, not the unfiltered scope total. Fire on a
  // short debounce so typing in the search box doesn't spam the counts
  // endpoint. Skip the very first run (handled by the totalCount effect above
  // on initial mount).
  const prevFilterParams = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (prevFilterParams.current === filterParams) return
    const prev = prevFilterParams.current
    prevFilterParams.current = filterParams
    if (prev === undefined) return // initial mount — totalCount effect handles it
    const t = setTimeout(() => { void refreshScopeTotals() }, 400)
    return () => clearTimeout(t)
  }, [refreshScopeTotals, filterParams])

  // Filter tab changed (All/Unread/Read/Starred/Closed/...). The badge must
  // now show the count for the newly-selected category per scope. `filter` is
  // intentionally NOT part of `filterParams` (the counts endpoint returns all
  // categories in one pass), so this needs its own trigger. Skip the initial
  // mount (handled by the totalCount effect).
  const prevFilter = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (prevFilter.current === urlFilter) return
    const prev = prevFilter.current
    prevFilter.current = urlFilter
    if (prev === undefined) return
    void refreshScopeTotals()
  }, [refreshScopeTotals, urlFilter])

  // Opens a conversation as its own in-app tab (AgentTabs), not a drawer —
  // matches how every other screen (Chats, Facebook chats, etc.) opens a
  // conversation. The tab's title is passed via navigate state so
  // AgentLayout's route-watching effect doesn't need an extra fetch just to
  // label it.
  const openConversationTab = (id: string, label?: string): void => {
    markRead([id])
    // Falls back to the conversation id so a tab opened without a known
    // interaction number is still identifiable (several can be open at once).
    navigate(`/agent/conversation/view/${id}`, { state: { tabLabel: label ?? id } })
  }

  // Deep link from a notification / search result: open that conversation.
  const processedRef = useRef<string | null>(null)
  useEffect(() => {
    const conversationId = searchParams.get('conversationId')
    if (!conversationId || processedRef.current === conversationId) return
    processedRef.current = conversationId
    const next = new URLSearchParams(searchParams)
    next.delete('conversationId')
    setSearchParams(next, { replace: true })
    // The notification carries no interaction number, so label the tab with the
    // conversation id unless the row is already in the loaded list.
    const row = conversations.find((c) => c.id === conversationId)
    openConversationTab(conversationId, row?.interactionId ? `#${row.interactionId}` : undefined)
  }, [searchParams, setSearchParams, markRead, navigate, conversations])

  // Apply the page saved by the restore effect — but only once the URL
  // actually reflects the view that page belongs to. The scope/filter writes
  // above may land in a later commit, and the effects they trigger (the
  // URL-filter sync's setFilter, the hook's baseQuery reset) force the page
  // to 1 — applying before they settle loses the page again.
  useEffect(() => {
    if (!viewLoaded || !restoredPageRef.current) return
    const restored = restoredPageRef.current
    if (restored.scope !== scope || restored.filter !== urlFilter) return
    restoredPageRef.current = null
    setPage(restored.page, restored.pageSize)
  }, [viewLoaded, scope, urlFilter, setPage])

  const updateParams = (changes: Record<string, string | null>): void => {
    const next = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === '') next.delete(k)
      else next.set(k, v)
    }
    setSearchParams(next, { replace: true })
  }

  const scopes: InboxScope[] = (Object.keys(SCOPE_META) as Scope[]).map((k) => ({
    key: k,
    label: SCOPE_META[k].label,
    icon: SCOPE_META[k].icon,
    count: scopeTotals[k],
  }))

  const handleSelect = (id: string): void => {
    const row = conversations.find((c) => c.id === id)
    openConversationTab(id, row?.interactionId ? `#${row.interactionId}` : undefined)
  }

  const dismissTour = (): void => {
    setFirstVisit(false)
    try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* noop */ }
  }

  const handleCreate = (_data: NewConversationData): void => {
    // The panel already persisted the conversation via POST /conversations;
    // re-fetch so the newly created one appears in the list.
    void refetch()
  }

  return (
    <>
      <ConversationInbox
        title="Inbox"
        subtitle={SCOPE_META[scope].subtitle}
        statusCounts={statusCounts}
        statusInfo={statusInfo}
        canAddChild
        scopes={scopes}
        activeScope={scope}
        onScopeChange={(next) => updateParams({ scope: next === defaultScope ? null : next })}
        conversations={conversations}
        totalCount={totalCount}
        filterCounts={filterCounts}
        loading={loading}
        currentPage={page}
        pageSize={pageSize}
        defaultFilter={urlFilter}
        onPageChange={setPage}
        onFilterChange={(next) => updateParams({ filter: next === 'all' ? null : next })}
        onSearchChange={setSearch}
        onDateRangeChange={setDateRange}
        initialFromDate={fromDate}
        initialToDate={toDate}
        onChannelChange={setChannel}
        onCampaignIdsChange={setCampaignIds}
        advancedFilters={advancedFilters}
        onAdvancedFiltersChange={setAdvancedFilters}
        onClearAdvancedFilters={clearAdvancedFilters}
        showChildTickets={showChildTickets}
        onShowChildTicketsChange={setShowChildTickets}
        showSecondaryTickets={showSecondaryTickets}
        onShowSecondaryTicketsChange={setShowSecondaryTickets}
        onToggleStar={toggleStar}
        onMarkRead={markRead}
        onMarkUnread={markUnread}
        onArchive={archive}
        onUnarchive={unarchive}
        onBulkArchive={bulkArchive}
        onBulkUnarchive={bulkUnarchive}
        clearSelectionSignal={clearSelectionSignal}
        onReassign={handleReassign}
        onBulkTransfer={handleBulkTransfer}
        canTransfer={canTransfer}
        canBulkTransfer={canBulkTransfer}
        canArchive={canArchive}
        onRefresh={() => void refetch()}
        onSelectConversation={handleSelect}
        onNewConversation={() => { /* the create panel is opened by the inbox itself */ }}
        onCreateConversation={handleCreate}
        isFirstVisit={firstVisit}
        onDismissOnboarding={dismissTour}
      />
      <ReassignDrawer
        open={drawerController.drawerOpen}
        onClose={drawerController.closeDrawer}
        selectedCount={drawerController.selectedCount}
        teams={dropdowns.teams}
        agents={dropdowns.agents}
        campaigns={dropdowns.campaigns}
        currentAssigneeAgentIds={drawerController.currentAssigneeAgentIds}
        submitting={drawerController.submitting}
        canBulkAssignment={canTransfer}
        canBulkTransfer={canBulkTransfer}
        canChangeStatus={canChangeStatus && !selectionHasParent}
        // The inbox's action is "Bulk Action": ownership *and* properties.
        // The standalone Reassignment page keeps the reassign-only drawer.
        showProperties
        onSubmit={drawerController.handleSubmit}
        onSubmitTransfer={drawerController.handleTransfer}
      />
    </>
  )
}
